import type {
	BaseItemSchema,
	OriginalItemTypes,
	ToeicArtifactType,
} from "@memorang/types";
import type { ComponentType } from "react";
import ToeicItemContainer from "../components/itemtypes/ToeicItemContainer";
import ChoiceContainer from "../components/itemtypes/pte/ChoiceContainer";
import FibDragDropContainer from "../components/itemtypes/pte/FibDragDropContainer";
import FibDropdownContainer from "../components/itemtypes/pte/FibDropdownContainer";
import FibTypeInContainer from "../components/itemtypes/pte/FibTypeInContainer";
import HighlightWordsContainer from "../components/itemtypes/pte/HighlightWordsContainer";
import IntroScreenContainer from "../components/itemtypes/pte/IntroScreenContainer";
import MultipleChoiceContainer from "../components/itemtypes/pte/MultipleChoiceContainer";
import ReorderContainer from "../components/itemtypes/pte/ReorderContainer";
import SpeakingContainer from "../components/itemtypes/pte/SpeakingContainer";
import WritingContainer from "../components/itemtypes/pte/WritingContainer";

export type ItemTypeContainerProps = {
	item: BaseItemSchema;
	isSplitLayout: boolean;
	isSummativeTest?: boolean;
};

export type ItemTypeContainer = ComponentType<ItemTypeContainerProps>;

const PTE_ITEM_TYPE_GROUPS: ReadonlyArray<
	readonly [ItemTypeContainer, readonly OriginalItemTypes[]]
> = [
	[IntroScreenContainer, ["INTRO_SCREEN"]],
	[MultipleChoiceContainer, ["MCSAR", "MCSAR_CORE", "MCMAR", "MCMAR_CORE"]],
	[
		ChoiceContainer,
		[
			"HCSL",
			"HCSL_CORE",
			"MCSAL",
			"MCSAL_CORE",
			"SMWL",
			"SMWL_CORE",
			"MCMAL",
			"MCMAL_CORE",
		],
	],
	[
		SpeakingContainer,
		[
			"ASQSW",
			"ASQSW_CORE",
			"DISW",
			"DISW_CORE",
			"RASW",
			"RASW_CORE",
			"RLSW",
			"RSSW",
			"RSSW_CORE",
			"RTSSW",
			"RTSSW_CORE",
			"SGDL",
			"PISW",
			"PISW_CORE",
		],
	],
	[
		WritingContainer,
		[
			"SSTL",
			"SSTL_CORE",
			"SWTR",
			"SWTR_CORE",
			"WESW",
			"WEMSW",
			"WEMSW_CORE",
			"WFDL",
			"WFDL_CORE",
		],
	],
	[FibTypeInContainer, ["FIBTIL", "FIBTIL_CORE"]],
	[FibDragDropContainer, ["FIBDDR", "FIBDDR_CORE"]],
	[FibDropdownContainer, ["FIBDPR", "FIBDPR_CORE"]],
	[HighlightWordsContainer, ["HIWL", "HIWL_CORE"]],
	[ReorderContainer, ["ROPR", "ROPR_CORE"]],
];

const TOEIC_ITEM_TYPES: readonly OriginalItemTypes[] = [
	"SINGLE_SELECT",
	"MULTI_SELECT",
	"BLANK_DRAG_WORD",
	"BLANK_DROPDOWN",
	"MATCHING_PAIR",
];

const itemTypeRegistry: Partial<Record<OriginalItemTypes, ItemTypeContainer>> =
	(() => {
		const registry: Partial<Record<OriginalItemTypes, ItemTypeContainer>> = {};
		for (const [container, keys] of PTE_ITEM_TYPE_GROUPS) {
			for (const key of keys) {
				registry[key] = container;
			}
		}
		for (const key of TOEIC_ITEM_TYPES) {
			registry[key] = ToeicItemContainer;
		}
		return registry;
	})();

const artifactTypeRegistry: Partial<
	Record<ToeicArtifactType, ItemTypeContainer>
> = {
	PHOTOGRAPH: ToeicItemContainer,
	CONVERSATION: ToeicItemContainer,
	TALKS: ToeicItemContainer,
	QUESTION_RESPONSE: ToeicItemContainer,
	SINGLE_PASSAGE: ToeicItemContainer,
	MULTIPLE_PASSAGE: ToeicItemContainer,
	TEXT_COMPLETION: ToeicItemContainer,
	INCOMPLETE_SENTENCES: ToeicItemContainer,
};

export const resolveItemTypeContainer = (
	item: BaseItemSchema,
): ItemTypeContainer | undefined => {
	const childType = item.metadata?.type;
	const artifactType = item.stimulusMetadata?.toeic_artifact_type;

	if (artifactType) {
		return artifactTypeRegistry[artifactType];
	}

	return childType ? itemTypeRegistry[childType] : undefined;
};
